import type { QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { getCurrentClinicCustomerId } from "@/lib/utils/clinic-context";

type OwnerRollbackQueueItem = {
  ownerId: string;
  userId: string;
  customerId: string;
  createdAt: string;
  expiresAt: string;
  attempts: number;
  lastAttemptAt?: string;
  lastErrorMessage?: string;
};

const STORAGE_KEY_PREFIX = "akuna.ownerRollbackQueue";
const LEGACY_STORAGE_KEY = "vet-clinic-spotlight.ownerRollbackQueue.v1";
const QUEUE_TTL_MS = 30 * 60 * 1000; // 30min
const MAX_ATTEMPTS_PER_ITEM = 6;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getErrorMessage(error: unknown): string | undefined {
  if (!error) return undefined;
  if (error instanceof Error) return error.message;
  if (typeof error === "object") {
    const maybe = error as Record<string, unknown>;
    if (typeof maybe.message === "string") return maybe.message;
  }
  return undefined;
}

function isBrowserStorageAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${userId}`;
}

function readQueue(userId: string): OwnerRollbackQueueItem[] {
  if (!isBrowserStorageAvailable()) return [];
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((item) => item && typeof item.ownerId === "string")
      .map((item) => ({
        ownerId: String(item.ownerId),
        userId: typeof item.userId === "string" ? item.userId : userId,
        customerId: typeof item.customerId === "string" ? item.customerId : "",
        createdAt: typeof item.createdAt === "string" ? item.createdAt : new Date().toISOString(),
        expiresAt:
          typeof item.expiresAt === "string"
            ? item.expiresAt
            : new Date(Date.now() + QUEUE_TTL_MS).toISOString(),
        attempts: Number.isFinite(Number(item.attempts)) ? Number(item.attempts) : 0,
        lastAttemptAt: typeof item.lastAttemptAt === "string" ? item.lastAttemptAt : undefined,
        lastErrorMessage: typeof item.lastErrorMessage === "string" ? item.lastErrorMessage : undefined,
      }));
  } catch {
    return [];
  }
}

function writeQueue(userId: string, items: OwnerRollbackQueueItem[]): void {
  if (!isBrowserStorageAvailable()) return;
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(items));
  } catch {
    // ignore
  }
}

async function resolveRollbackScope(): Promise<{ userId: string; customerId: string } | null> {
  const { data } = await supabase.auth.getUser();
  const userId = data.user?.id ?? null;
  if (!userId) return null;

  try {
    const customerId = await getCurrentClinicCustomerId();
    if (!customerId) return null;
    return { userId, customerId };
  } catch {
    return null;
  }
}

function isExpired(item: Pick<OwnerRollbackQueueItem, "expiresAt">): boolean {
  const t = Date.parse(item.expiresAt);
  if (!Number.isFinite(t)) return true;
  return Date.now() > t;
}

async function shouldDropQueueItem(
  item: OwnerRollbackQueueItem,
  scope: { userId: string; customerId: string },
): Promise<boolean> {
  if (item.userId !== scope.userId) return true;
  if (item.customerId !== scope.customerId) return false; // keep until the user returns to that clinic
  if (isExpired(item)) return true;
  if ((item.attempts ?? 0) >= MAX_ATTEMPTS_PER_ITEM) return true;

  // If the owner now has pets, we should never auto-delete it.
  // If we can't read it due to RLS/network, keep and retry later (safer than deleting).
  const { data, error } = await supabase
    .from("owners")
    .select(
      `
      id,
      deleted_at,
      created_by,
      owner_customer:owners_customers!inner(customer_id, deleted_at),
      pets:pets(id, deleted_at)
    `,
    )
    .eq("id", item.ownerId)
    .maybeSingle();

  if (error) return false;
  if (!data) return true;

  const owner = data as unknown as {
    deleted_at: string | null;
    created_by: string | null;
    owner_customer: Array<{ customer_id: string; deleted_at: string | null }> | null;
    pets: Array<{ id: string; deleted_at: string | null }> | null;
  };

  if (owner.deleted_at) return true;
  if (owner.created_by && owner.created_by !== scope.userId) return true;

  const links = Array.isArray(owner.owner_customer) ? owner.owner_customer : [];
  const hasActiveLink = links.some((l) => l.customer_id === scope.customerId && l.deleted_at === null);
  if (!hasActiveLink) return true;

  const pets = Array.isArray(owner.pets) ? owner.pets : [];
  const hasActivePet = pets.some((p) => p.deleted_at === null);
  if (hasActivePet) return true;

  return false;
}

export async function enqueueOwnerRollback(ownerId: string, lastErrorMessage?: string): Promise<void> {
  if (!ownerId) return;
  const scope = await resolveRollbackScope();
  if (!scope) return;

  // Clean up legacy key so we don't accumulate stale entries (v2 is user-scoped).
  try {
    window.localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch {
    // ignore
  }

  const existing = readQueue(scope.userId);
  if (existing.some((item) => item.ownerId === ownerId)) return;

  const createdAt = new Date().toISOString();
  const expiresAt = new Date(Date.now() + QUEUE_TTL_MS).toISOString();

  writeQueue(scope.userId, [
    ...existing,
    {
      ownerId,
      userId: scope.userId,
      customerId: scope.customerId,
      createdAt,
      expiresAt,
      attempts: 0,
      lastErrorMessage,
    },
  ]);
}

export async function rollbackOwnerWithRetry(
  ownerId: string,
  opts?: { maxAttempts?: number; baseDelayMs?: number },
): Promise<{ ok: true } | { ok: false; lastError: unknown }> {
  const maxAttempts = Math.max(1, opts?.maxAttempts ?? 3);
  const baseDelayMs = Math.max(0, opts?.baseDelayMs ?? 350);

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const { error } = await supabase.rpc("soft_delete_owner", { p_owner_id: ownerId });
      if (error) throw error;
      return { ok: true };
    } catch (err) {
      lastError = err;
      if (attempt < maxAttempts) {
        const delay = baseDelayMs * Math.pow(2, attempt - 1);
        await sleep(delay);
      }
    }
  }

  return { ok: false, lastError };
}

export async function rollbackOwnerOrQueue(
  ownerId: string,
  opts?: { queryClient?: QueryClient; maxAttempts?: number },
): Promise<{ status: "rolled_back" } | { status: "queued"; error: unknown }> {
  const result = await rollbackOwnerWithRetry(ownerId, { maxAttempts: opts?.maxAttempts ?? 3 });
  if (result.ok === true) {
    if (opts?.queryClient) invalidatePatientsCache(opts.queryClient, { ownerId });
    return { status: "rolled_back" };
  }

  await enqueueOwnerRollback(ownerId, getErrorMessage(result.lastError));
  return { status: "queued", error: result.lastError };
}

export async function processOwnerRollbackQueue(opts?: {
  queryClient?: QueryClient;
  maxItemsPerRun?: number;
}): Promise<void> {
  const scope = await resolveRollbackScope();
  if (!scope) return;

  const maxItemsPerRun = Math.max(1, opts?.maxItemsPerRun ?? 5);
  const queue = readQueue(scope.userId);
  if (queue.length === 0) return;

  const remaining: OwnerRollbackQueueItem[] = [];
  let processed = 0;

  for (const item of queue) {
    if (processed >= maxItemsPerRun) {
      remaining.push(item);
      continue;
    }

    const drop = await shouldDropQueueItem(item, scope);
    if (drop) continue;

    if (item.customerId !== scope.customerId) {
      remaining.push(item);
      continue;
    }

    processed++;
    const attemptResult = await rollbackOwnerWithRetry(item.ownerId, { maxAttempts: 1, baseDelayMs: 0 });
    if (attemptResult.ok === true) {
      if (opts?.queryClient) invalidatePatientsCache(opts.queryClient, { ownerId: item.ownerId });
      continue;
    }

    const maybeMsg = getErrorMessage(attemptResult.lastError) ?? item.lastErrorMessage;

    remaining.push({
      ...item,
      attempts: (item.attempts ?? 0) + 1,
      lastAttemptAt: new Date().toISOString(),
      lastErrorMessage: maybeMsg,
    });
  }

  writeQueue(scope.userId, remaining);
}
